import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Nav } from "@/components/landing/Nav";
import { Footer } from "@/components/landing/Footer";

const SUPPORT_EMAIL = (process.env.REACT_APP_SUPPORT_EMAIL || "").trim();

const PolicyLayout = ({ title, updated, children, testid }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `${title} — NALURA`;
  }, [title]);

  return (
    <div className="bg-[#FBF7EE] text-[#211B10] min-h-screen" data-testid={testid}>
      <Nav />
      <main className="pt-32 md:pt-40 pb-24 max-w-3xl mx-auto px-6 md:px-12">
        <Link to="/" data-testid="policy-back-link" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#211B10]/60 hover:text-[#A67C1E] transition-colors mb-10">
          <ArrowLeft className="w-3.5 h-3.5" /> Kembali ke Beranda
        </Link>
        <h1 className="font-display tracking-tighter text-4xl sm:text-5xl mb-3">{title}</h1>
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A67C1E] mb-12">Terakhir diperbarui: {updated}</p>
        <div className="space-y-8 text-sm md:text-base text-[#211B10]/70 leading-relaxed [&_h2]:font-display [&_h2]:text-2xl [&_h2]:tracking-tight [&_h2]:text-[#211B10] [&_h2]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-2">
          {children}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export const PrivacyPolicy = () => (
  <PolicyLayout title="Kebijakan Privasi" updated="Juni 2026" testid="privacy-page">
    <section>
      <h2>Informasi yang Kami Kumpulkan</h2>
      <p>NALURA adalah situs kurasi affiliate. Data yang kami kumpulkan:</p>
      <ul>
        <li><strong>Data statistik anonim</strong> — jumlah klik pada tombol produk beserta sumber trafiknya (misalnya WhatsApp, Telegram, atau kunjungan langsung) untuk keperluan analisis internal.</li>
        <li><strong>Nama dan email</strong> — hanya jika Anda mengisi form panduan gratis dan mencentang persetujuan. Kami gunakan untuk mengirim panduan dan email dari NALURA.</li>
        <li><strong>Nama dan nomor WhatsApp</strong> — hanya jika Anda mengisi form pre-order, untuk keperluan konfirmasi pesanan.</li>
        <li><strong>Data perilaku anonim</strong> — halaman yang dikunjungi dan tombol yang diklik, melalui alat analitik (lihat bagian Cookie & Pelacakan).</li>
      </ul>
    </section>
    <section>
      <h2>Cookie & Alat Pelacakan</h2>
      <p>
        Situs ini dapat menggunakan cookie dan alat pelacakan pihak ketiga — <strong>Meta Pixel</strong> dan{" "}
        <strong>Google Analytics 4</strong> — untuk memahami bagaimana pengunjung menggunakan situs dan untuk
        mengukur efektivitas promosi kami. Alat ini mencatat data perilaku secara agregat/anonim (halaman yang dibuka,
        tombol yang diklik). Anda dapat menolak cookie melalui pengaturan browser Anda. Cookie sesi juga digunakan
        pada area admin untuk autentikasi pemilik situs.
      </p>
    </section>
    <section>
      <h2>Penyimpanan & Penghapusan Data</h2>
      <p>
        Nama dan email yang Anda daftarkan kami simpan selama Anda masih berlangganan, atau hingga Anda meminta
        penghapusan. Anda dapat <strong>berhenti berlangganan kapan saja</strong> melalui tautan berhenti langganan
        di email kami, atau <strong>meminta penghapusan seluruh data Anda</strong> dengan menghubungi kami — permintaan
        diproses maksimal 7 hari kerja.
      </p>
    </section>
    <section>
      <h2>Transaksi & Pihak Ketiga</h2>
      <p>
        Seluruh proses pembelian, pembayaran, dan pengiriman produk digital dilakukan sepenuhnya di platform resmi
        penyedia program (pihak ketiga). Data yang Anda berikan saat bertransaksi tunduk pada kebijakan privasi
        platform tersebut, bukan kebijakan kami. Kami menyarankan Anda membaca kebijakan privasi penyedia sebelum
        bertransaksi.
      </p>
    </section>
    <section>
      <h2>Keamanan</h2>
      <p>
        Kami menerapkan praktik keamanan yang wajar untuk melindungi data yang kami simpan dan tidak pernah menjual
        atau membagikan data pribadi Anda kepada pihak lain untuk tujuan pemasaran mereka.
      </p>
    </section>
    <section>
      <h2>Hubungi Kami</h2>
      <p>
        Untuk pertanyaan tentang data pribadi Anda — termasuk permintaan penghapusan — hubungi kami melalui halaman{" "}
        <Link to="/kontak" className="underline text-[#A67C1E]">Kontak</Link>
        {SUPPORT_EMAIL ? <> atau email <a href={`mailto:${SUPPORT_EMAIL}`} className="underline text-[#A67C1E]">{SUPPORT_EMAIL}</a></> : null}.
      </p>
    </section>
  </PolicyLayout>
);

export const Terms = () => (
  <PolicyLayout title="Syarat & Ketentuan" updated="Juni 2026" testid="terms-page">
    <section>
      <h2>Tentang Layanan Kami</h2>
      <p>
        NALURA ("kami") adalah situs kurasi dan rekomendasi program pengembangan diri. Kami adalah mitra affiliate
        resmi — sebagian tautan di situs ini adalah tautan affiliate, dan kami dapat menerima komisi dari pembelian
        yang Anda lakukan <strong>tanpa biaya tambahan apa pun bagi Anda</strong>.
      </p>
    </section>
    <section>
      <h2>Peran Kami sebagai Affiliate</h2>
      <ul>
        <li>Kami bukan penjual, pengembang, atau penyedia produk yang direkomendasikan.</li>
        <li>Transaksi terjadi antara Anda dan penyedia program di platform resmi mereka.</li>
        <li>Harga, ketersediaan, garansi, dan kebijakan pengembalian dana sepenuhnya ditentukan oleh penyedia program.</li>
      </ul>
    </section>
    <section>
      <h2>Batasan Tanggung Jawab</h2>
      <p>
        Deskripsi produk di situs ini disusun berdasarkan informasi dari penyedia dan pengalaman pengguna. Hasil setiap
        individu dapat berbeda-beda. Program pengembangan diri bukan pengganti nasihat medis, psikologis, atau keuangan
        profesional. Kami tidak bertanggung jawab atas kerugian yang timbul dari penggunaan produk pihak ketiga.
      </p>
    </section>
    <section>
      <h2>Testimoni</h2>
      <p>
        Testimoni yang ditampilkan merupakan pengalaman individu dan tidak menjamin hasil yang sama bagi setiap orang.
      </p>
    </section>
    <section>
      <h2>Hak Kekayaan Intelektual</h2>
      <p>
        Nama NALURA, logo, tagline "Good Mind Good Life", serta seluruh konten tulisan di situs ini adalah milik kami
        dan tidak boleh digunakan tanpa izin tertulis. Merek dan materi produk yang direkomendasikan adalah milik
        penyedia masing-masing.
      </p>
    </section>
    <section>
      <h2>Email & Berhenti Berlangganan</h2>
      <p>
        Jika Anda berlangganan email kami, Anda dapat berhenti kapan saja melalui tautan berhenti langganan di setiap
        email atau dengan menghubungi kami. Kami tidak mengirim spam dan tidak membagikan email Anda ke pihak lain.
      </p>
    </section>
    <section>
      <h2>Perubahan Ketentuan</h2>
      <p>
        Kami dapat memperbarui syarat & ketentuan ini sewaktu-waktu. Versi terbaru selalu tersedia di halaman ini.
      </p>
    </section>
  </PolicyLayout>
);
